import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NavbarV2 from './navbar-v2';
import FooterV2 from './footer-v2';
import { API_BASE_URL } from '../config';


interface Video {
  _id: string;
  title: string;
  description: string;
  category: string;
  mimeType: string;
  fileSize: number;
  fileSizeMB: string;
  viewCount: number;
  streamUrl: string;
  createdAt: string;
}

export default function Egitim() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/videos?category=egitim`);
      const data = await res.json();
      if (data.success && data.videos) {
        setVideos(data.videos);
      }
    } catch (err) {
      console.error('Video yükleme hatası:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <NavbarV2 />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-50 to-teal-50 py-16 pt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-5xl mb-4">🎓</div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Eğitim Videoları</h1>
            <p className="text-lg text-gray-600 mb-6">
              Hatim Takip uygulamasının özelliklerini, bildirim ayarlarını ve kullanımını adım adım öğrenin
            </p>
            <div className="flex items-center justify-center text-sm text-gray-500">
              <Link to="/" className="hover:text-red-500">Ana Sayfa</Link>
              <span className="mx-2">/</span>
              <span className="text-red-500">Eğitim Videoları</span>
            </div>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">

            {/* Loading State */}
            {loading && (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-emerald-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-700">Videolar yükleniyor...</h3>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Videolar yüklenemedi</h3>
                <p className="text-gray-500 mb-4">Lütfen daha sonra tekrar deneyin.</p>
                <button
                  onClick={() => { setLoading(true); setError(false); loadVideos(); }}
                  className="inline-flex items-center px-6 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition"
                >
                  Tekrar Dene
                </button>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && videos.length === 0 && (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <span className="text-3xl">🎬</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz video eklenmemiş</h3>
                <p className="text-gray-500">Eğitim videoları yakında eklenecektir.</p>
              </div>
            )}

            {/* Video Cards — Solda bilgi, sağda video */}
            {!loading && !error && videos.map((video, idx) => (
              <div
                key={video._id}
                className={`flex flex-col lg:flex-row items-stretch bg-white border rounded-2xl overflow-hidden shadow-lg mb-10 hover:shadow-xl transition-shadow ${
                  idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Sol — Başlık & Açıklama */}
                <div className="lg:w-5/12 p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">
                      {idx + 1}
                    </span>
                    <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Eğitim Videosu</span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-800 mb-3 leading-tight">{video.title}</h3>

                  {video.description && (
                    <p className="text-gray-600 mb-5 leading-relaxed">{video.description}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-auto">
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {video.fileSizeMB} MB
                    </span>
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {video.viewCount || 0} görüntüleme
                    </span>
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(video.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>

                {/* Sağ — Video Player */}
                <div className="lg:w-7/12 bg-black flex items-center justify-center overflow-hidden" style={{ maxHeight: '480px' }}>
                  <video
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-contain"
                    style={{ maxHeight: '480px' }}
                    src={`${API_BASE_URL}${video.streamUrl}`}
                  >
                    Tarayıcınız video etiketini desteklemiyor.
                  </video>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Info Cards Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 text-center mb-10">Uygulama Özellikleri</h2>
            <div className="grid md:grid-cols-3 gap-6">

              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-emerald-500">
                <div className="text-3xl mb-3">🔔</div>
                <h5 className="text-lg font-semibold text-gray-800 mb-2">Bildirimler</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Namaz vakitleri ve hatırlatıcılar için bildirim ayarlarını yapılandırın. 
                  Bildirimlerin düzgün çalışması için arka plan izinlerinin aktif olması gerekir.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-emerald-500">
                <div className="text-3xl mb-3">👥</div>
                <h5 className="text-lg font-semibold text-gray-800 mb-2">Grup Hatim</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Arkadaşlarınızla birlikte hatim grubu oluşturun, cüz paylaşımı yapın 
                  ve birbirinizin ilerlemesini takip edin.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-emerald-500">
                <div className="text-3xl mb-3">📖</div>
                <h5 className="text-lg font-semibold text-gray-800 mb-2">Okuma Takibi</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Kur'an-ı Kerim, Risale-i Nur ve diğer eserlerin okumanızı takip edin, 
                  notlar alın ve ilerlemenizi görün.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <FooterV2 />
    </div>
  );
}
